// public/js/user/userObject.js
import BaseObject from '@base/baseObject.js';

class UserObject extends BaseObject {
    constructor(data = {}) {
        const userSchema = {
            email: {
                type: 'email',
                required: true,
                default: '',
                maxLength: 255,
                label: 'Email Address',
                section: 'basic_info',
                showInForm: true,
                showInList: true,
                filterable: false,
                helpText: 'Your email address for login and notifications'
            },
            firstName: {
                type: 'string',
                required: true,
                default: '',
                minLength: 2,
                maxLength: 50,
                label: 'First Name',
                section: 'basic_info',
                showInForm: true,
                showInList: true,
                filterable: false,
                helpText: 'Your first name'
            },
            lastName: {
                type: 'string',
                required: true,
                default: '',
                minLength: 2,
                maxLength: 50,
                label: 'Last Name',
                section: 'basic_info',
                showInForm: true,
                showInList: true,
                filterable: false,
                helpText: 'Your last name'
            },
            gender: {
                type: 'select',
                required: false,
                default: 'prefer_not_to_say',
                options: [
                    { value: 'prefer_not_to_say', text: 'Keine Angabe' },
                    { value: 'male', text: 'Männlich' },
                    { value: 'female', text: 'Weiblich' },
                    { value: 'other', text: 'Andere' }
                ],
                label: 'Geschlecht',
                section: 'basic_info',
                showInForm: true,
                showInList: false,
                showInDetails: true,
                filterable: false,
                helpText: 'Your gender (optional)'
            },
            role: {
                type: 'select',
                required: true,
                default: 'user',
                options: [
                    { value: 'user', text: 'User' },
                    { value: 'pro', text: 'Pro User' },
                    { value: 'admin', text: 'Administrator' }
                ],
                label: 'Role',
                section: 'permissions',
                showInForm: false, // Not editable by user
                showInList: true,
                showInDetails: true,
                filterable: false,
                helpText: 'User role and permission level'
            },
            emailVerified: {
                type: 'boolean',
                required: false,
                default: false,
                label: 'Email Verified',
                section: 'status_info',
                showInForm: false,
                showInList: false,
                showInDetails: true,
                filterable: false,
                helpText: 'Whether email address has been verified'
            },
            adminActivated: {
                type: 'boolean',
                required: false,
                default: false,
                label: 'Admin Activated',
                section: 'status_info',
                showInForm: false,
                showInList: false,
                showInDetails: true,
                filterable: false,
                helpText: 'Whether account has been activated by administrator'
            },
            createdAt: {
                type: 'datetime-local',
                dateFormat: 'date-time',
                required: false,
                default: '',
                label: 'Created Date',
                section: 'system_info',
                showInForm: false,
                showInList: false,
                showInDetails: true,
                filterable: false,
                helpText: 'When the account was created'
            },
            updatedAt: {
                type: 'datetime-local',
                dateFormat: 'date-time',
                required: false,
                default: '',
                label: 'Last Updated',
                section: 'system_info',
                showInForm: false,
                showInList: false,
                showInDetails: true,
                filterable: false,
                helpText: 'When the account was last updated'
            },
            lastLogin: {
                type: 'datetime-local',
                dateFormat: 'date-time',
                required: false,
                default: null,
                label: 'Last Login',
                section: 'system_info',
                showInForm: false,
                showInList: false,
                showInDetails: true,
                filterable: false,
                helpText: 'When the user last logged in'
            },
            isLocked: {
                type: 'boolean',
                required: false,
                default: false,
                label: 'Account Locked',
                section: 'status_info',
                showInForm: false,
                showInList: false,
                showInDetails: true,
                filterable: false,
                helpText: 'Whether account is locked due to failed login attempts'
            },
            failedAttempts: {
                type: 'number',
                required: false,
                default: 0,
                label: 'Failed Login Attempts',
                section: 'status_info',
                showInForm: false,
                showInList: false,
                showInDetails: true,
                filterable: false,
                helpText: 'Number of recent failed login attempts'
            },
            photo_url: {
                type: 'string',
                required: false,
                default: '',
                maxLength: 500,
                label: 'Photo URL',
                section: 'system_info',
                showInForm: false,
                showInList: false,
                showInDetails: false,
                filterable: false
            }
        };

        super(data, userSchema, {
            entityType: 'user',
            generateId: () => crypto.randomUUID()
        });
    }

    /**
     * Get available sections for this entity
     * @returns {Object} Section definitions with display names
     */
    static getSectionDefinitions() {
        return {
            basic_info: {
                title: 'Basic Information',
                order: 1,
                description: 'Personal information'
            },
            permissions: {
                title: 'Permissions & Role',
                order: 2,
                description: 'User permissions and access level'
            },
            status_info: {
                title: 'Account Status',
                order: 3,
                description: 'Account verification and activation status'
            },
            system_info: {
                title: 'System Information',
                order: 4,
                description: 'Account creation and login history'
            }
        };
    }
}

export default UserObject;